import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

/**
 * AhuComponentSelect picks one component of the air handling unit data.
 *
 * The value is the `componentId` of the component data, the stable
 * reference of the component itself. Items, assessments and photos all
 * reference the component by it, not by the id of the component data,
 * which belongs to one air handling unit data instance only.
 *
 * A component removed from the air handling unit data is still shown, so
 * the referencing row stays readable and can be pointed at another
 * component.
 *
 * Given a `fieldName`, it is marked invalid while the key has errors in the
 * nearest ErrValidationFailedContextProvider and resets them on change.
 */
export function AhuComponentSelect({
  ahuDataId,
  componentId,
  onComponentIdChange,
  disabled,
  className,
  fieldName,
}: {
  ahuDataId: UUID;
  componentId: UUID;
  onComponentIdChange: (componentId: UUID) => void;
  disabled?: boolean;
  className?: string;
  fieldName?: string;
}) {
  const { data } = useHtzAirHandlingUnitDataShowQuery({ id: ahuDataId });
  const { invalid, reset } = useFieldErrors(fieldName);

  const components = [...(data?.components ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const isRemoved =
    componentId !== "" &&
    data !== undefined &&
    !components.some((component) => component.componentId === componentId);

  return (
    <Select
      value={componentId}
      onValueChange={(value) => {
        reset();
        onComponentIdChange(value);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn("w-full", className)}
        aria-invalid={invalid || undefined}
      >
        <SelectValue placeholder={t("Komponente wählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {isRemoved && (
            <SelectItem value={componentId}>
              {t("(Komponente entfernt)")}
            </SelectItem>
          )}
          {components.map((component) => (
            <SelectItem value={component.componentId} key={component.id}>
              {component.name}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
